// src/pages/Headquarter/ViewHeadquarters.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import headquarterService from '../../services/headquarter.service';
import '../../styles/ViewPages.css';

const ViewHeadquarters = () => {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();

  const [headquarters, setHeadquarters] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchHeadquarters();
  }, [currentPage, searchTerm]);

  const fetchHeadquarters = async () => {
    setLoading(true);
    try {
      const companyId = isSuperAdmin 
        ? localStorage.getItem('selectedCompanyId')
        : user?.companyId;

      if (!companyId) {
        alert('Please select a company first');
        setLoading(false);
        return;
      }

      const response = await headquarterService.getHeadquarters(
        companyId,
        currentPage,
        10,
        searchTerm
      );

      if (response.success && response.data) {
        setHeadquarters(response.data.headquarters || []);
        setTotalPages(response.data.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching headquarters:', error);
      alert('Failed to fetch headquarters');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        const response = await headquarterService.deleteHeadquarter(id);
        if (response.success) {
          alert('Headquarter deleted successfully!');
          fetchHeadquarters();
        }
      } catch (error) {
        alert(error.response?.data?.message || 'Failed to delete headquarter');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  return (
    <div className="view-headquarters-container">
      {/* Header */}
      <div className="view-header">
        <h4 className="view-title">View Headquarters</h4>
        <div className="view-actions">
          <button
            className="btn-change-hq"
            onClick={() => navigate('/headquarter/change')}
          >
            Change HQ/Sub HQ
          </button>
          <button
            className="btn-add"
            onClick={() => navigate('/headquarter/add')}
          >
            + Add Headquarter
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <input
          type="text"
          className="search-input"
          placeholder="Search headquarters by company... 🔍"
          value={searchTerm}
          onChange={handleSearch}
        />
      </div>

      {/* Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading">Loading headquarters...</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>COMPANY</th>
                <th>REGION</th>
                <th>HEADQUARTER</th>
                <th>ADDED DATE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {headquarters.length > 0 ? (
                headquarters.map((hq) => (
                  <tr key={hq.id}>
                    <td>{hq.company?.name || 'N/A'}</td>
                    <td>{hq.region?.name || 'N/A'}</td>
                    <td>{hq.name}</td>
                    <td>{formatDate(hq.createdAt)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-view"
                          onClick={() => navigate(`/headquarter/view/${hq.id}`)}
                          title="View Details"
                        >
                          View
                        </button>
                        <button
                          className="btn-edit"
                          onClick={() => navigate(`/headquarter/edit/${hq.id}`)}
                          title="Edit Headquarter"
                        >
                          Edit
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => handleDelete(hq.id, hq.name)}
                          title="Delete Headquarter"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="no-data">
                    {searchTerm ? 'No headquarters found matching your search' : 'No headquarters available'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            « Previous
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            Next »
          </button>
        </div>
      )}
    </div>
  );
};

export default ViewHeadquarters;